// Icon — the sanctioned icon wrapper (milestone-02 Phase 3).
//
// Implements design-language.md §9: ONE icon set (Lucide), rendered through
// this wrapper only, on a locked size grid with one optical language:
//
//   - `size` is a token union: 16 | 20 | 24 (default 16). Sub-grid sizes
//     (12/14px) round UP to 16 — that is the point of the grid.
//   - `strokeWidth` is locked at 2 with `absoluteStrokeWidth`, so the stroke
//     is 2px at every grid size (no more 1.5/1.75/2/2.5 scatter — §9 ⚖S7).
//     The `strokeWidth` prop exists ONLY as a documented escape hatch for
//     hero icons (EmptyState/ErrorState 48px, PlaceholderCard 32px use 1.5);
//     everything else keeps the default.
//   - Decorative by default: `aria-hidden` unless `label` is passed, in
//     which case the svg gets `role="img"` + `aria-label` instead.
//
// Off-grid sizes (32/48px heroes): pass the size via className (`h-8 w-8`,
// `h-12 w-12`) — CSS width/height wins over the svg's width/height
// attributes, so the class overrides the `size` prop. Pair it with
// `size={24}` (the largest grid step) so `absoluteStrokeWidth`'s stroke math
// stays proportional: e.g. size={24} strokeWidth={1.5} className="h-8 w-8"
// renders a 2px stroke at 32px — identical to raw lucide
// `<Glyph size={32} strokeWidth={1.5} />`.
//
// Glyphs enter through `./registry` — the only sanctioned door (ESLint
// `no-restricted-imports` bans `lucide-react` everywhere else).

import type { LucideIcon, LucideProps } from 'lucide-react';
import { cn } from '@/lib/cn';

/** The locked icon size grid (design-language.md §9). */
export type IconSize = 16 | 20 | 24;

export interface IconProps extends Omit<
  LucideProps,
  'size' | 'strokeWidth' | 'absoluteStrokeWidth' | 'ref'
> {
  /** The glyph to render — import it from '@/components/Icon' (the registry). */
  icon: LucideIcon;
  /** Grid size in px. Sub-grid usages (12/14px) round up to 16. */
  size?: IconSize;
  /**
   * Accessible name. When present the icon renders `role="img"` +
   * `aria-label`; when absent it is decorative (`aria-hidden`).
   */
  label?: string;
  /**
   * ESCAPE HATCH — locked at 2 by default. Only sanctioned deviation is 1.5
   * on hero icons (48px EmptyState/ErrorState, 32px PlaceholderCard).
   */
  strokeWidth?: number;
}

export function Icon({
  icon: Glyph,
  size = 16,
  label,
  strokeWidth = 2,
  className,
  ...rest
}: IconProps): JSX.Element {
  const a11yProps = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true } as const);
  return (
    <Glyph
      size={size}
      strokeWidth={strokeWidth}
      absoluteStrokeWidth
      className={cn(className)}
      {...a11yProps}
      {...rest}
    />
  );
}
